import React, { useState } from "react";
import {
    Modal,
    ModalBody,
    Nav,
    NavItem,
    NavLink,
    TabContent,
    TabPane,
    Row,
    Col,
    Button
} from "reactstrap";
import { FILES_URL, BASE_URL } from "helpers/url_helper";
import RatesOfPayModal from "./RatesOfPayModal";
import formatPhone from "constants/formatPhone";
import formatDate from "constants/formatDate";
import RequestDocumentModal from "./RequestDocumentModal";
import ManualUploadModal from "./ManualUploadModal";
import UploadDocumentSelectorModal from "./UploadDocumentSelectorModal";
import RequestDocumentSelectorModal from "./RequestDocumentSelectorModal";

const badgeStyle = (bg) => ({
    background: bg,
    padding: "6px 14px",
    borderRadius: "20px",
    fontSize: 13,
    color: "white",
    fontWeight: 600,
});

const EmployeeFilesModal = ({ user, isOpen, toggle, reloadData }) => {
    const [activeTab, setActiveTab] = useState("driver");
    const [showRatesModal, setShowRatesModal] = useState(false);
    const [requestModalOpen, setRequestModalOpen] = useState(false);
    const [requestDocFile, setRequestDocFile] = useState(null);
    const [manualUploadOpen, setManualUploadOpen] = useState(false);
    const [manualFile, setManualFile] = useState(null);
    const [manualRawText, setManualRawText] = useState("");
    const [manualPreview, setManualPreview] = useState("");
    const [selectorOpen, setSelectorOpen] = useState(false);
    const [selectorRequestOpen, setSelectorRequestOpen] = useState(false);
    const [requestedDocs, setRequestedDocs] = useState([]);

    const handleOpenFile = (type, fileId) => {
        try {
            const authUser = JSON.parse(localStorage.getItem("authUser") || "{}");
            const token = authUser?.token || "";

            const url = `${BASE_URL}/users/signed-files/${type}/${fileId}/${token}`;
            window.open(url, "_blank", "noopener");
        } catch (e) {
            console.error("Failed to open file", e);
        }
    };

    const handleManualUpload = (file) => {
        setManualFile(file);
        setManualUploadOpen(true);
    };

    const handleRequestDocument = (file) => {
        setRequestedDocs([]);
        setRequestDocFile(file);
        setRequestModalOpen(true);
    };

    const renderFileCard = (file, title, extraLines = [], type, flashUnsigned = false) => {
        const expired = file.driver_license_expiration_date && new Date(file.driver_license_expiration_date) < new Date();
        const isFixed = file.is_expiration_fixed === 1;

        const flashClass =
            (expired && !isFixed) || flashUnsigned
                ? "flash-expired"
                : "";

        return (
            <Col md={3} sm={4} xs={6} key={file.id} className="mb-4">
                <div
                    onClick={() => handleOpenFile(type, file.id)}
                    className={flashClass}
                    style={{
                        cursor: "pointer",
                        background: "#2f2f2f",
                        borderRadius: "14px",
                        padding: "18px",
                        textAlign: "center",
                        border: "1px solid rgba(255,255,255,0.08)",
                        boxShadow: "0 2px 6px rgba(0,0,0,0.25)"
                    }}
                >
                    <div style={{ fontSize: 36, marginBottom: 6, color: "#ffd86b" }}>
                        <i className="bx bxs-file-blank" />
                    </div>

                    <div style={{ fontWeight: 700, color: "white", marginBottom: 6 }}>
                        {title}
                    </div>

                    {extraLines.filter(Boolean).map((line, idx) => (
                        <div key={idx} style={{ color: "#bbb", fontSize: 11 }}>
                            {line}
                        </div>
                    ))}

                    {expired && isFixed && (
                        <div
                            style={{
                                marginTop: 12,
                                color: "#7ed957",
                                fontSize: 12,
                                fontWeight: 600
                            }}
                        >
                            Fixed by {file.expiration_fixed_by?.full_name} on{" "}
                            {formatDate(file.updated_at)}
                        </div>
                    )}

                    {expired && !isFixed && (
                        <div style={{ marginTop: 14 }}>
                            <div
                                style={{
                                    background: "#2980b9",
                                    color: "white",
                                    padding: "4px 10px",
                                    borderRadius: 12,
                                    marginBottom: 6,
                                    fontSize: 12,
                                    cursor: "pointer"
                                }}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleRequestDocument(file);
                                }}
                            >
                                Request Document
                            </div>

                            <div
                                style={{
                                    background: "#e67e22",
                                    color: "white",
                                    padding: "4px 10px",
                                    borderRadius: 12,
                                    fontSize: 12,
                                    cursor: "pointer"
                                }}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleManualUpload(file);
                                }}
                            >
                                Manual Upload
                            </div>
                        </div>
                    )}
                </div>
            </Col>
        );
    };

    const daysLater = (date) => {
        const d = new Date(date);
        const now = new Date();
        const diff = Math.round((d - now) / (1000 * 60 * 60 * 24));
        return diff;
    };

    const NoFiles = ({ text }) => (
        <div
            style={{
                textAlign: "center",
                padding: "60px 0",
                color: "white",
                fontSize: "26px",
                fontWeight: 700,
                opacity: 0.9,
                letterSpacing: "0.5px"
            }}
        >
            {text}
        </div>
    );

    const signedMap = {};
    user.signed_employee_files?.forEach(f => {
        if (f.is_signed) signedMap[f.employee_file_id] = true;
    });

    const filteredEmployeeFiles = user.signed_employee_files?.filter(f => {
        if (f.is_signed) return true;
        return !signedMap[f.employee_file_id];
    });

    return (
        <>
            <Modal
                isOpen={isOpen}
                toggle={toggle}
                contentClassName="custom-dark-modal"
                style={{
                    maxWidth: "1000px",
                    background: "transparent"
                }}
            >
                <div
                    onClick={toggle}
                    style={{
                        position: "absolute",
                        right: 5,
                        top: 5,
                        width: 36,
                        height: 36,
                        borderRadius: "50%",
                        background: "#3a3a3a",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        color: "#fff",
                        fontSize: 20,
                        cursor: "pointer",
                        boxShadow: "0 2px 8px rgba(0,0,0,0.4)",
                        zIndex: 10
                    }}
                >
                    ✕
                </div>

                <ModalBody
                    style={{
                        background: "#262523",
                        borderRadius: "5px",
                        paddingTop: "40px"
                    }}
                >
                    <div
                        style={{
                            display: "flex",
                            alignItems: "center",
                            background: "#3a3a3a",
                            padding: "20px",
                            borderRadius: "16px",
                            marginBottom: "25px",
                            color: "white",
                            justifyContent: "space-between",
                        }}
                    >

                        {/* LEFT SECTION: IMAGE + NAME + SECONDARY INFO */}
                        <div style={{ display: "flex", alignItems: "center", gap: "18px" }}>

                            {/* Profile Image with Gold Ring */}
                            <div
                                style={{
                                    width: 80,
                                    height: 80,
                                    borderRadius: "50%",
                                    overflow: "hidden",
                                    border: "3px solid #d4af37"
                                }}
                            >
                                <img
                                    src={user.picture ? FILES_URL + user.picture : "https://placehold.co/80"}
                                    alt="profile"
                                    style={{ width: "100%", height: "100%", objectFit: "cover" }}
                                    onError={(e) => {
                                        e.target.onerror = null;
                                        e.target.src = "https://placehold.co/80";
                                    }}
                                />
                            </div>

                            {/* NAME + EMAIL */}
                            <div>
                                <div style={{ fontSize: 26, fontWeight: 700 }}>
                                    {user.first_name} {user.last_name}
                                </div>

                                <div
                                    style={{
                                        fontSize: 16,
                                        opacity: 0.8,
                                        marginTop: 6
                                    }}
                                >
                                    {user.email}
                                </div>
                            </div>
                        </div>

                        {/* RIGHT SECTION: BADGES */}
                        <div
                            style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "10px",
                                flexWrap: "wrap",
                                justifyContent: "flex-end",
                                maxWidth: "50%"
                            }}
                        >
                            {/* Active badge (always for onboarding) */}
                            <span style={badgeStyle("#2ecc71")}>Active</span>

                            {/* Gender */}
                            <span style={badgeStyle("#8e44ad")}>
                                {user.gender === "M" ? "Male" : user.gender === "F" ? "Female" : "Unknown"}
                            </span>

                            {/* Phone */}
                            <span style={badgeStyle("#2980b9")}>{formatPhone(user.phone_number)}</span>

                            {/* SSN */}
                            <span style={badgeStyle("#e67e22")}>SSN: {user.ssn}</span>

                            <Button
                                style={{ ...badgeStyle("#ffd86b"), color: "#000" }}
                                onClick={() => setShowRatesModal(true)}
                            >
                                Update Rates of Pay
                            </Button>

                            {/* DOB */}
                            <span style={badgeStyle("#d35400")}>{formatDate(user.date_of_birth)}</span>
                        </div>
                    </div>

                    <div style={{ textAlign: "center", marginBottom: 20 }}>
                        <Button
                            style={{
                                background: "#ffd86b",
                                color: "#000",
                                fontWeight: 700,
                                borderRadius: 12,
                                padding: "8px 18px",
                                marginRight: 10
                            }}
                            onClick={() => setSelectorOpen(true)}
                        >
                            Upload Document
                        </Button>

                        <Button
                            style={{
                                background: "#3aa5e5",
                                color: "#fff",
                                fontWeight: 700,
                                borderRadius: 12,
                                padding: "8px 18px"
                            }}
                            onClick={() => setSelectorRequestOpen(true)}
                        >
                            Request Document
                        </Button>
                    </div>

                    <Nav
                        tabs
                        style={{
                            display: "flex",
                            justifyContent: "center",
                            gap: "14px",
                            marginBottom: "20px",
                            borderBottom: "none"
                        }}
                    >
                        {[
                            ["driver", "Driver Licenses"],
                            ["ssn", "SSNs"],
                            ["birth", "Birth Certificates"],
                            ["passport", "Passports"],
                            ["gc", "Green Cards"],
                            ["ead", "EAD Cards"],
                            ["others", "Others"]
                        ].map(([id, label]) => (
                            <NavItem key={id}>
                                <NavLink
                                    onClick={() => setActiveTab(id)}
                                    className={activeTab === id ? "active" : ""}
                                    style={{
                                        cursor: "pointer",
                                        background: activeTab === id ? "#ffd86b" : "#444",
                                        color: activeTab === id ? "#000" : "#ccc",
                                        borderRadius: 10,
                                        padding: "8px 16px",
                                        border: "none",
                                        fontWeight: 600,
                                        boxShadow: activeTab === id ? "0 2px 6px rgba(0,0,0,0.3)" : "none"
                                    }}
                                >
                                    {label}
                                </NavLink>
                            </NavItem>
                        ))}
                    </Nav>

                    <TabContent activeTab={activeTab}>
                        {/* Driver Licenses */}
                        <TabPane tabId="driver">
                            {user.driver_licenses?.length ? (
                                <Row>
                                    {user.driver_licenses.map((dl) =>
                                        renderFileCard(
                                            dl,
                                            dl.driver_license_number || "Driver License",
                                            [
                                                dl.driver_license_expiration_date
                                                    ? `Exp: ${formatDate(dl.driver_license_expiration_date)} (${daysLater(dl.driver_license_expiration_date)} days ${daysLater(dl.driver_license_expiration_date) < 0 ? 'ago' : 'later'})`
                                                    : "",
                                                dl.street
                                                    ? `${dl.street}, ${dl.city || ""} ${dl.state || ""} ${dl.zip_code || ""}`
                                                    : ""
                                            ],
                                            "driver_licenses"
                                        )
                                    )}
                                </Row>
                            ) : (
                                <NoFiles text="No driver licenses uploaded." />
                            )}
                        </TabPane>

                        {/* SSNs */}
                        <TabPane tabId="ssn">
                            {user.ssn_files?.length ? (
                                <Row>
                                    {user.ssn_files.map((s) =>
                                        renderFileCard(
                                            s,
                                            "Social Security Card",
                                            [
                                                s.issuing_authority ? `Issuer: ${s.issuing_authority}` : "",
                                                s.restriction_text
                                            ],
                                            "ssn-files"
                                        )
                                    )}
                                </Row>
                            ) : (
                                <NoFiles text="No SSN files uploaded." />
                            )}
                        </TabPane>

                        {/* Birth Certificates */}
                        <TabPane tabId="birth">
                            {user.birth_certificates?.length ? (
                                <Row>
                                    {user.birth_certificates.map((bc) =>
                                        renderFileCard(bc, "Birth Certificate", [], "birth-certificates")
                                    )}
                                </Row>
                            ) : (
                                <NoFiles text="No birth certificates uploaded." />
                            )}
                        </TabPane>

                        {/* Passports */}
                        <TabPane tabId="passport">
                            {user.passport_files?.length ? (
                                <Row>
                                    {user.passport_files.map((p) =>
                                        renderFileCard(
                                            p,
                                            "Passport",
                                            [
                                                p.passport_number ? `Number: ${p.passport_number}` : "",
                                                p.expiration_date ? `Exp: ${formatDate(p.expiration_date)} (${daysLater(p.expiration_date)} days ${daysLater(p.expiration_date) < 0 ? 'ago' : 'later'})` : ""
                                            ],
                                            "passports"
                                        )
                                    )}
                                </Row>
                            ) : (
                                <NoFiles text="No passport files uploaded." />
                            )}
                        </TabPane>

                        {/* Green Cards */}
                        <TabPane tabId="gc">
                            {user.green_cards?.length ? (
                                <Row>
                                    {user.green_cards.map((gc) =>
                                        renderFileCard(
                                            gc,
                                            "Green Card",
                                            [
                                                gc.uscis_number ? `USCIS: ${gc.uscis_number}` : "",
                                                gc.expiration_date ? `Exp: ${formatDate(gc.expiration_date)} (${daysLater(gc.expiration_date)} days ${daysLater(gc.expiration_date) < 0 ? 'ago' : 'later'})` : ""
                                            ],
                                            "green-cards"
                                        )
                                    )}
                                </Row>
                            ) : (
                                <NoFiles text="No green cards uploaded." />
                            )}
                        </TabPane>

                        {/* EAD Cards */}
                        <TabPane tabId="ead">
                            {user.ead_cards?.length ? (
                                <Row>
                                    {user.ead_cards.map((ead) =>
                                        renderFileCard(
                                            ead,
                                            "EAD Card",
                                            [
                                                ead.a_number ? `A#: ${ead.a_number}` : "",
                                                ead.expiration_date ? `Exp: ${formatDate(ead.expiration_date)} (${daysLater(ead.expiration_date)} days ${daysLater(ead.expiration_date) < 0 ? 'ago' : 'later'})` : "",
                                                ead.issuing_authority ? `Issuer: ${ead.issuing_authority}` : ""
                                            ],
                                            "ead-cards"
                                        )
                                    )}
                                </Row>
                            ) : (
                                <NoFiles text="No EAD cards uploaded." />
                            )}
                        </TabPane>

                        {/* Others */}
                        <TabPane tabId="others">
                            {user.signed_employee_files?.length ? (
                                <Row>
                                    {filteredEmployeeFiles.map((f) =>
                                        renderFileCard(
                                            f,
                                            f.employee_file.file_title,
                                            [
                                                f.is_signed ? "" : "Not Signed",
                                                f.created_at ? `${f.is_signed ? 'Signed' : 'Created'}: ${formatDate(f.created_at)}` : ""
                                            ],
                                            "onboarding",
                                            f.is_signed ? false : true
                                        )
                                    )}
                                </Row>
                            ) : (
                                <NoFiles text="No other files uploaded." />
                            )}
                        </TabPane>
                    </TabContent>
                </ModalBody>
            </Modal>

            <RatesOfPayModal
                isOpen={showRatesModal}
                toggle={() => setShowRatesModal(false)}
                user={user}
            />

            <RequestDocumentModal
                isOpen={requestModalOpen}
                toggle={() => setRequestModalOpen(false)}
                user={user}
                file={requestDocFile}
                files={requestedDocs}
            />

            <ManualUploadModal
                isOpen={manualUploadOpen}
                toggle={() => setManualUploadOpen(false)}
                user={user}
                file={manualFile}
                rawText={manualRawText}
                setRawText={setManualRawText}
                preview={manualPreview}
                setPreview={setManualPreview}
                reloadData={reloadData}
            />

            <UploadDocumentSelectorModal
                isOpen={selectorOpen}
                toggle={() => setSelectorOpen(false)}
                onSelect={(docType) => {
                    setManualFile({ type: docType });
                    setManualUploadOpen(true);
                }}
            />

            <RequestDocumentSelectorModal
                isOpen={selectorRequestOpen}
                toggle={() => setSelectorRequestOpen(false)}
                onConfirm={(docs) => {
                    setRequestedDocs(docs);
                    setRequestDocFile(null);
                    setRequestModalOpen(true);
                }}
            />
        </>
    );
};

export default EmployeeFilesModal;